import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { CurrencyInput } from "@/components/ui/currency-input";
import { PageHeader } from "@/components/ui/page-header";
import {
  paymentsApi,
  companyApi,
  type PaymentQuoteResult,
  type ParsePaymentInvoiceResponse,
} from "@/services/api";
import { WIRE_COUNTRIES, SEPA_COUNTRIES } from "@/lib/countries";
import {
  Send,
  ArrowLeft,
  Upload,
  FileText,
  Check,
  Loader2,
  AlertCircle,
  RefreshCw,
  CheckCircle2,
  ArrowRight,
  Clock,
  Users,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type Step = "invoice" | "beneficiary" | "confirm";

const steps: { key: Step; label: string; description: string }[] = [
  { key: "invoice", label: "Invoice", description: "Dados do pagamento" },
  { key: "beneficiary", label: "Beneficiário", description: "Quem recebe" },
  { key: "confirm", label: "Confirmar", description: "Revise e envie" },
];

const currencyRailMap: Record<string, { rail: string; label: string }> = {
  USD: { rail: "wire", label: "Wire Transfer" },
  EUR: { rail: "sepa", label: "SEPA" },
  MXN: { rail: "spei", label: "SPEI" },
};

function formatFiatAmount(value: number | string | undefined, currency: string): string {
  if (!value) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  const localeMap: Record<string, string> = { USD: "en-US", EUR: "de-DE", MXN: "es-MX" };
  return new Intl.NumberFormat(localeMap[currency] || "en-US", {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatUsdc(value: number | string | undefined): string {
  if (!value) return "0.00";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "0.00";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(num);
}

interface InvoiceFormData {
  currency: string;
  amount: string;
  amountCents: number;
  invoiceNumber: string;
  supplierName: string;
  description: string;
}

interface InlineBeneficiaryData {
  beneficiaryName: string;
  // Wire
  routingNumber: string;
  bankAccountNumber: string;
  bankName: string;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressPostalCode: string;
  addressCountry: string;
  // SEPA
  iban: string;
  bic: string;
  sepaCountry: string;
  sepaType: string;
  firstName: string;
  lastName: string;
  // SPEI
  speiClabe: string;
  speiBankCode: string;
}

const emptyInlineBeneficiary: InlineBeneficiaryData = {
  beneficiaryName: "",
  routingNumber: "",
  bankAccountNumber: "",
  bankName: "",
  addressStreet: "",
  addressCity: "",
  addressState: "",
  addressPostalCode: "",
  addressCountry: "",
  iban: "",
  bic: "",
  sepaCountry: "",
  sepaType: "business",
  firstName: "",
  lastName: "",
  speiClabe: "",
  speiBankCode: "",
};

export function NovoPagamentoPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>("invoice");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Invoice data
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileBase64, setUploadedFileBase64] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<ParsePaymentInvoiceResponse | null>(null);
  const [formData, setFormData] = useState<InvoiceFormData>({
    currency: "USD",
    amount: "",
    amountCents: 0,
    invoiceNumber: "",
    supplierName: "",
    description: "",
  });

  // Step 2: Beneficiary
  const [beneficiaryMode, setBeneficiaryMode] = useState<"saved" | "inline">("inline");
  const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState<string>("");
  const [inlineBeneficiary, setInlineBeneficiary] = useState<InlineBeneficiaryData>(emptyInlineBeneficiary);
  const [saveBeneficiary, setSaveBeneficiary] = useState(false);
  const [beneficiaryNickname, setBeneficiaryNickname] = useState("");

  // Step 3: Quote
  const [quote, setQuote] = useState<PaymentQuoteResult | null>(null);
  const [quoteCountdown, setQuoteCountdown] = useState(30);
  const [isRefreshingQuote, setIsRefreshingQuote] = useState(false);
  const [companyNotes, setCompanyNotes] = useState("");
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch beneficiaries
  const { data: beneficiaries } = useQuery({
    queryKey: ["payment-beneficiaries"],
    queryFn: () => paymentsApi.listBeneficiaries(),
  });

  // Fetch USDC balance
  const { data: dashboardData } = useQuery({
    queryKey: ["company-dashboard"],
    queryFn: () => companyApi.dashboard.get(),
  });

  const usdcAvailable = (dashboardData?.balance?.usdcBalance || 0) - (dashboardData?.balance?.usdcLockedBalance || 0);

  const paymentRail = currencyRailMap[formData.currency]?.rail || "wire";

  // Filter beneficiaries by currency
  const filteredBeneficiaries = (beneficiaries || []).filter(
    (b) => b.currency === formData.currency && b.isActive
  );

  // File upload handler
  const handleFileUpload = useCallback(
    async (file: File) => {
      setError(null);
      setLoading(true);
      setUploadedFile(file);

      try {
        const reader = new FileReader();
        const fileContent = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.split(",")[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        setUploadedFileBase64(fileContent);

        const result = await paymentsApi.parseInvoice({
          fileBase64: fileContent,
          fileName: file.name,
          fileMimeType: file.type,
        });

        setParseResult(result);

        if (result.success && result.data) {
          const parsedAmount = result.data.amount || 0;
          const parsedCents = Math.round(parsedAmount * 100);
          const parsedCurrency = result.data.currency || "USD";
          const localeMap: Record<string, string> = { USD: "en-US", EUR: "de-DE", MXN: "es-MX" };
          const locale = localeMap[parsedCurrency] || "en-US";
          const formattedAmount = parsedAmount > 0
            ? parsedAmount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : "";
          setFormData({
            currency: parsedCurrency,
            amount: formattedAmount,
            amountCents: parsedCents,
            invoiceNumber: result.data.invoiceNumber || "",
            supplierName: result.data.supplierName || "",
            description: result.data.description || "",
          });

          // Pre-fill beneficiary data if available
          if (result.data.beneficiaryName) {
            setInlineBeneficiary((prev) => ({
              ...prev,
              beneficiaryName: result.data?.beneficiaryName || "",
              bankName: result.data?.bankName || "",
              bankAccountNumber: result.data?.bankAccountNumber || "",
              routingNumber: result.data?.routingNumber || "",
              iban: result.data?.iban || "",
              bic: result.data?.bic || "",
              speiClabe: result.data?.clabe || "",
            }));
          }
        }
      } catch (err) {
        console.error("Erro ao processar arquivo:", err);
        setError("Erro ao processar o arquivo. Preencha os dados manualmente.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Quote refresh
  const fetchQuote = useCallback(async () => {
    try {
      setIsRefreshingQuote(true);
      const result = await paymentsApi.getQuote(formData.currency);
      setQuote(result);
      setQuoteCountdown(30);
    } catch (err) {
      console.error("Erro ao obter cotação:", err);
      toast.error("Erro ao obter cotação", {
        description: "Tente novamente em instantes.",
        style: { background: "#495565", color: "#ffffff" },
      });
    } finally {
      setIsRefreshingQuote(false);
    }
  }, [formData.currency]);

  // Quote auto-refresh countdown
  useEffect(() => {
    if (currentStep !== "confirm" || !quote) return;

    countdownRef.current = setInterval(() => {
      setQuoteCountdown((prev) => {
        if (prev <= 1) {
          fetchQuote();
          return 30;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [currentStep, quote, fetchQuote]);

  // Step navigation
  const goToStep = async (step: Step) => {
    if (step === "confirm") {
      await fetchQuote();
    }
    setCurrentStep(step);
  };

  // Calculate fees
  const amount = formData.amountCents / 100;
  const serviceFee = quote?.serviceFee
    ? quote.serviceFee.type === "FIXED"
      ? quote.serviceFee.rate
      : amount * quote.serviceFee.rate
    : 0;
  const networkFee = quote?.providerFlatFeeUsd || 0;
  const totalFiatWithFees = amount + serviceFee;
  const amountUsdc = quote?.rate ? totalFiatWithFees / quote.rate : 0;

  // Submit payment
  const handleSubmit = async () => {
    setError(null);
    setLoading(true);

    try {
      const payload: Parameters<typeof paymentsApi.createPayment>[0] = {
        currency: formData.currency,
        paymentRail,
        amount,
        invoiceNumber: formData.invoiceNumber || undefined,
        supplierName: formData.supplierName || undefined,
        description: formData.description || undefined,
        invoiceFileBase64: uploadedFileBase64 || undefined,
        invoiceFileName: uploadedFile?.name || undefined,
        invoiceFileMimeType: uploadedFile?.type || undefined,
        companyNotes: companyNotes || undefined,
      };

      if (beneficiaryMode === "saved" && selectedBeneficiaryId) {
        payload.beneficiaryId = selectedBeneficiaryId;
      } else {
        payload.inlineBeneficiaryName = inlineBeneficiary.beneficiaryName || undefined;

        if (paymentRail === "wire") {
          payload.inlineRoutingNumber = inlineBeneficiary.routingNumber || undefined;
          payload.inlineBankAccountNumber = inlineBeneficiary.bankAccountNumber || undefined;
          payload.inlineBankName = inlineBeneficiary.bankName || undefined;
          payload.inlineAddressStreet = inlineBeneficiary.addressStreet || undefined;
          payload.inlineAddressCity = inlineBeneficiary.addressCity || undefined;
          payload.inlineAddressState = inlineBeneficiary.addressState || undefined;
          payload.inlineAddressPostalCode = inlineBeneficiary.addressPostalCode || undefined;
          payload.inlineAddressCountry = inlineBeneficiary.addressCountry || undefined;
        } else if (paymentRail === "sepa") {
          payload.inlineIban = inlineBeneficiary.iban || undefined;
          payload.inlineBic = inlineBeneficiary.bic || undefined;
          payload.inlineSepaCountry = inlineBeneficiary.sepaCountry || undefined;
          payload.inlineSepaType = inlineBeneficiary.sepaType || undefined;
          payload.inlineFirstName = inlineBeneficiary.firstName || undefined;
          payload.inlineLastName = inlineBeneficiary.lastName || undefined;
        } else if (paymentRail === "spei") {
          payload.inlineSpeiClabe = inlineBeneficiary.speiClabe || undefined;
          payload.inlineSpeiBankCode = inlineBeneficiary.speiBankCode || undefined;
        }

        if (saveBeneficiary && beneficiaryNickname) {
          payload.saveBeneficiary = true;
          payload.beneficiaryNickname = beneficiaryNickname;
        }
      }

      const result = await paymentsApi.createPayment(payload);

      toast.success("Pagamento solicitado", {
        description: `Código: ${result.paymentCode}. Aguardando aprovação.`,
        style: { background: "#495565", color: "#ffffff" },
      });

      navigate(`/pagamentos/${result.id}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Erro desconhecido";
      setError(message);
      toast.error("Erro ao criar pagamento", {
        description: message,
        style: { background: "#495565", color: "#ffffff" },
      });
    } finally {
      setLoading(false);
    }
  };

  // Validation per step
  const canProceedStep1 =
    formData.currency && formData.amountCents > 0 && formData.supplierName;

  const canProceedStep2 =
    beneficiaryMode === "saved"
      ? !!selectedBeneficiaryId
      : !!inlineBeneficiary.beneficiaryName &&
        (paymentRail === "wire"
          ? !!inlineBeneficiary.routingNumber && !!inlineBeneficiary.bankAccountNumber
          : paymentRail === "sepa"
          ? !!inlineBeneficiary.iban
          : paymentRail === "spei"
          ? !!inlineBeneficiary.speiClabe
          : false);

  const canSubmit = quote && amountUsdc > 0 && amountUsdc <= usdcAvailable;

  // Step indicator
  const stepIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Send className="h-5 w-5 sm:h-6 sm:w-6" />}
        title="Novo Pagamento Internacional"
        description="Envie um pagamento para o exterior"
        actions={
          <Button
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => navigate("/pagamentos")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        }
      />

      {/* Step indicator */}
      <div className="flex items-center gap-2 sm:gap-4">
        {steps.map((step, index) => (
          <div key={step.key} className="flex items-center gap-2 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={cn(
                  "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                  index < stepIndex
                    ? "bg-green-100 text-green-700"
                    : index === stepIndex
                    ? "bg-blue-600 text-white"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {index < stepIndex ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  index + 1
                )}
              </div>
              <div className="min-w-0 hidden sm:block">
                <p className="text-sm font-medium truncate">{step.label}</p>
                <p className="text-xs text-muted-foreground truncate">{step.description}</p>
              </div>
            </div>
            {index < steps.length - 1 && (
              <div className="flex-1 h-px bg-border hidden sm:block" />
            )}
          </div>
        ))}
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* ─── Step 1: Invoice / Payment Data ─── */}
      {currentStep === "invoice" && (
        <div className="space-y-4">
          {/* Upload area */}
          <Card>
            <CardHeader>
              <CardTitle>Upload da Invoice (opcional)</CardTitle>
              <CardDescription>
                Envie o PDF da invoice para preenchimento automático via IA
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!uploadedFile ? (
                <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-muted-foreground/25 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                  <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                  <span className="text-sm text-muted-foreground">
                    Arraste ou clique para enviar
                  </span>
                  <span className="text-xs text-muted-foreground mt-1">PDF, max 10MB</span>
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                  />
                </label>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                  <FileText className="w-8 h-8 text-blue-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{uploadedFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {loading ? (
                        <span className="flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" /> Extraindo dados...
                        </span>
                      ) : parseResult?.success ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <Check className="w-3 h-3" /> Dados extraídos (confiança: {Math.round((parseResult.confidence || 0) * 100)}%)
                        </span>
                      ) : (
                        <span className="text-yellow-600">Preencha manualmente</span>
                      )}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setUploadedFile(null);
                      setUploadedFileBase64(null);
                      setParseResult(null);
                    }}
                  >
                    Remover
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment data form */}
          <Card>
            <CardHeader>
              <CardTitle>Dados do Pagamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Moeda *</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, currency: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD - Dólar Americano (Wire)</SelectItem>
                      <SelectItem value="EUR">EUR - Euro (SEPA)</SelectItem>
                      <SelectItem value="MXN">MXN - Peso Mexicano (SPEI)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Valor *</Label>
                  <CurrencyInput
                    currency={formData.currency as "USD" | "EUR" | "MXN"}
                    value={formData.amount}
                    onChange={(formatted, cents) => {
                      setFormData((prev) => ({ ...prev, amount: formatted, amountCents: cents }));
                    }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Número da Invoice</Label>
                  <Input
                    placeholder="INV-12345"
                    value={formData.invoiceNumber}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, invoiceNumber: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label>Nome do Fornecedor *</Label>
                  <Input
                    placeholder="Acme Inc."
                    value={formData.supplierName}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, supplierName: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div>
                <Label>Descrição</Label>
                <Textarea
                  placeholder="Descrição do pagamento..."
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                  rows={3}
                />
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => goToStep("beneficiary")}
                  disabled={!canProceedStep1}
                >
                  Continuar
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── Step 2: Beneficiary ─── */}
      {currentStep === "beneficiary" && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Beneficiário</CardTitle>
              <CardDescription>
                Selecione um beneficiário cadastrado ou preencha os dados
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Mode toggle */}
              <div className="flex gap-2">
                <Button
                  variant={beneficiaryMode === "saved" ? "default" : "outline"}
                  onClick={() => setBeneficiaryMode("saved")}
                  disabled={filteredBeneficiaries.length === 0}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Cadastrado ({filteredBeneficiaries.length})
                </Button>
                <Button
                  variant={beneficiaryMode === "inline" ? "default" : "outline"}
                  onClick={() => setBeneficiaryMode("inline")}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo
                </Button>
              </div>

              {/* Saved beneficiary selection */}
              {beneficiaryMode === "saved" && (
                <div>
                  <Label>Selecione o beneficiário</Label>
                  <Select
                    value={selectedBeneficiaryId}
                    onValueChange={setSelectedBeneficiaryId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredBeneficiaries.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.nickname} - {b.beneficiaryName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Inline beneficiary fields - Wire */}
              {beneficiaryMode === "inline" && paymentRail === "wire" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Nome do Beneficiário *</Label>
                      <Input
                        value={inlineBeneficiary.beneficiaryName}
                        onChange={(e) =>
                          setInlineBeneficiary((prev) => ({
                            ...prev,
                            beneficiaryName: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label>Nome do Banco</Label>
                      <Input
                        value={inlineBeneficiary.bankName}
                        onChange={(e) =>
                          setInlineBeneficiary((prev) => ({
                            ...prev,
                            bankName: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Routing Number *</Label>
                      <Input
                        placeholder="021000089"
                        maxLength={9}
                        value={inlineBeneficiary.routingNumber}
                        onChange={(e) =>
                          setInlineBeneficiary((prev) => ({
                            ...prev,
                            routingNumber: e.target.value.replace(/\D/g, ""),
                          }))
                        }
                        error={inlineBeneficiary.routingNumber && inlineBeneficiary.routingNumber.length !== 9 ? "Deve ter 9 dígitos" : undefined}
                      />
                      <p className="text-xs text-muted-foreground mt-1">9 dígitos — encontrado no cheque ou site do banco</p>
                    </div>
                    <div>
                      <Label>Número da Conta *</Label>
                      <Input
                        maxLength={17}
                        value={inlineBeneficiary.bankAccountNumber}
                        onChange={(e) =>
                          setInlineBeneficiary((prev) => ({
                            ...prev,
                            bankAccountNumber: e.target.value.replace(/\D/g, ""),
                          }))
                        }
                        error={inlineBeneficiary.bankAccountNumber && (inlineBeneficiary.bankAccountNumber.length < 4 || inlineBeneficiary.bankAccountNumber.length > 17) ? "Entre 4 e 17 dígitos" : undefined}
                      />
                    </div>
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">Endereço</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Rua</Label>
                      <Input
                        value={inlineBeneficiary.addressStreet}
                        onChange={(e) =>
                          setInlineBeneficiary((prev) => ({
                            ...prev,
                            addressStreet: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label>Cidade</Label>
                      <Input
                        value={inlineBeneficiary.addressCity}
                        onChange={(e) =>
                          setInlineBeneficiary((prev) => ({
                            ...prev,
                            addressCity: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label>Estado</Label>
                      <Input
                        placeholder="NY"
                        maxLength={2}
                        value={inlineBeneficiary.addressState}
                        onChange={(e) =>
                          setInlineBeneficiary((prev) => ({
                            ...prev,
                            addressState: e.target.value.toUpperCase(),
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label>ZIP Code</Label>
                      <Input
                        placeholder="10001"
                        maxLength={10}
                        value={inlineBeneficiary.addressPostalCode}
                        onChange={(e) =>
                          setInlineBeneficiary((prev) => ({
                            ...prev,
                            addressPostalCode: e.target.value.replace(/[^0-9-]/g, ""),
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label>País *</Label>
                      <SearchableSelect
                        options={WIRE_COUNTRIES.map((c) => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
                        value={inlineBeneficiary.addressCountry}
                        onChange={(v) => setInlineBeneficiary((prev) => ({ ...prev, addressCountry: v }))}
                        placeholder="Selecione o país"
                        searchPlaceholder="Buscar país..."
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Inline beneficiary fields - SEPA */}
              {beneficiaryMode === "inline" && paymentRail === "sepa" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Nome do Beneficiário *</Label>
                      <Input
                        value={inlineBeneficiary.beneficiaryName}
                        onChange={(e) =>
                          setInlineBeneficiary((prev) => ({
                            ...prev,
                            beneficiaryName: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label>Tipo</Label>
                      <Select
                        value={inlineBeneficiary.sepaType}
                        onValueChange={(v) =>
                          setInlineBeneficiary((prev) => ({ ...prev, sepaType: v }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="business">Empresa</SelectItem>
                          <SelectItem value="individual">Pessoa Física</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {inlineBeneficiary.sepaType === "individual" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Nome</Label>
                        <Input
                          value={inlineBeneficiary.firstName}
                          onChange={(e) =>
                            setInlineBeneficiary((prev) => ({
                              ...prev,
                              firstName: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Label>Sobrenome</Label>
                        <Input
                          value={inlineBeneficiary.lastName}
                          onChange={(e) =>
                            setInlineBeneficiary((prev) => ({
                              ...prev,
                              lastName: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>IBAN *</Label>
                      <Input
                        placeholder="DE89 3704 0044 0532 0130 00"
                        maxLength={34}
                        value={inlineBeneficiary.iban}
                        onChange={(e) =>
                          setInlineBeneficiary((prev) => ({
                            ...prev,
                            iban: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                          }))
                        }
                        error={inlineBeneficiary.iban && (inlineBeneficiary.iban.length < 15 || inlineBeneficiary.iban.length > 34) ? "IBAN deve ter entre 15 e 34 caracteres" : undefined}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Código internacional da conta bancária</p>
                    </div>
                    <div>
                      <Label>BIC / SWIFT</Label>
                      <Input
                        placeholder="COBADEFFXXX"
                        maxLength={11}
                        value={inlineBeneficiary.bic}
                        onChange={(e) =>
                          setInlineBeneficiary((prev) => ({
                            ...prev,
                            bic: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                          }))
                        }
                        error={inlineBeneficiary.bic && inlineBeneficiary.bic.length !== 8 && inlineBeneficiary.bic.length !== 11 ? "Deve ter 8 ou 11 caracteres" : undefined}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>País *</Label>
                    <SearchableSelect
                      options={SEPA_COUNTRIES.map((c) => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
                      value={inlineBeneficiary.sepaCountry}
                      onChange={(v) => setInlineBeneficiary((prev) => ({ ...prev, sepaCountry: v }))}
                      placeholder="Selecione o país"
                      searchPlaceholder="Buscar país..."
                    />
                  </div>
                </div>
              )}

              {/* Inline beneficiary fields - SPEI */}
              {beneficiaryMode === "inline" && paymentRail === "spei" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Nome do Beneficiário *</Label>
                      <Input
                        value={inlineBeneficiary.beneficiaryName}
                        onChange={(e) =>
                          setInlineBeneficiary((prev) => ({
                            ...prev,
                            beneficiaryName: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label>CLABE *</Label>
                      <Input
                        placeholder="012345678901234567"
                        maxLength={18}
                        value={inlineBeneficiary.speiClabe}
                        onChange={(e) =>
                          setInlineBeneficiary((prev) => ({
                            ...prev,
                            speiClabe: e.target.value.replace(/\D/g, ""),
                          }))
                        }
                        error={inlineBeneficiary.speiClabe && inlineBeneficiary.speiClabe.length !== 18 ? `${inlineBeneficiary.speiClabe.length}/18 dígitos` : undefined}
                      />
                      <p className="text-xs text-muted-foreground mt-1">18 dígitos — Clave Bancaria Estandarizada</p>
                    </div>
                  </div>
                  <div>
                    <Label>Código do Banco</Label>
                    <Input
                      placeholder="012"
                      maxLength={3}
                      value={inlineBeneficiary.speiBankCode}
                      onChange={(e) =>
                        setInlineBeneficiary((prev) => ({
                          ...prev,
                          speiBankCode: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              {/* Save beneficiary checkbox */}
              {beneficiaryMode === "inline" && (
                <div className="border-t pt-4 space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={saveBeneficiary}
                      onChange={(e) => setSaveBeneficiary(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">
                      Salvar este beneficiário para próximos pagamentos
                    </span>
                  </label>
                  {saveBeneficiary && (
                    <div>
                      <Label>Apelido do beneficiário</Label>
                      <Input
                        placeholder="Ex: Fornecedor ABC - USD"
                        value={beneficiaryNickname}
                        onChange={(e) => setBeneficiaryNickname(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setCurrentStep("invoice")}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
                <Button
                  onClick={() => goToStep("confirm")}
                  disabled={!canProceedStep2}
                >
                  Continuar
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── Step 3: Confirmation ─── */}
      {currentStep === "confirm" && (
        <div className="space-y-4">
          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Resumo do Pagamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Fornecedor</p>
                  <p className="font-medium">{formData.supplierName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Invoice</p>
                  <p className="font-medium">{formData.invoiceNumber || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Moeda / Rail</p>
                  <p className="font-medium">
                    {formData.currency} - {currencyRailMap[formData.currency]?.label}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Beneficiário</p>
                  <p className="font-medium">
                    {beneficiaryMode === "saved"
                      ? filteredBeneficiaries.find((b) => b.id === selectedBeneficiaryId)
                          ?.beneficiaryName || "-"
                      : inlineBeneficiary.beneficiaryName || "-"}
                  </p>
                </div>
              </div>
              {formData.description && (
                <div>
                  <p className="text-sm text-muted-foreground">Descrição</p>
                  <p className="text-sm">{formData.description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quote and fee breakdown */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Cotação e Custos</CardTitle>
                <CardDescription>
                  {quote ? (
                    <span className="flex items-center gap-1">
                      1 USDC = {quote.rate.toFixed(4)} {formData.currency}
                      <span className="text-xs text-muted-foreground ml-2">
                        Atualiza em {quoteCountdown}s
                      </span>
                    </span>
                  ) : (
                    "Carregando cotação..."
                  )}
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchQuote}
                disabled={isRefreshingQuote}
              >
                <RefreshCw
                  className={cn("w-4 h-4", isRefreshingQuote && "animate-spin")}
                />
              </Button>
            </CardHeader>
            <CardContent>
              {!quote ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Valor do pagamento</span>
                    <span className="font-medium">
                      {formatFiatAmount(amount, formData.currency)}
                    </span>
                  </div>

                  {serviceFee > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        Taxa de serviço
                        <span className="text-xs">
                          ({quote.serviceFee?.description || ""})
                        </span>
                      </span>
                      <span className="font-medium">
                        {formatFiatAmount(serviceFee, formData.currency)}
                      </span>
                    </div>
                  )}

                  {networkFee > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Taxa da rede ({currencyRailMap[formData.currency]?.label})
                      </span>
                      <span className="font-medium">
                        ${networkFee.toFixed(2)} USD
                      </span>
                    </div>
                  )}

                  <div className="border-t pt-3 flex justify-between items-center">
                    <span className="font-semibold">Total estimado em USDC</span>
                    <span className="text-lg font-bold text-blue-600">
                      {formatUsdc(amountUsdc)} USDC
                    </span>
                  </div>

                  {quote.estimatedDelivery && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>Prazo estimado: {quote.estimatedDelivery}</span>
                    </div>
                  )}

                  <div className="border-t pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Saldo USDC disponível
                      </span>
                      <span
                        className={cn(
                          "font-medium",
                          amountUsdc > usdcAvailable
                            ? "text-red-600"
                            : "text-green-600"
                        )}
                      >
                        {formatUsdc(usdcAvailable)} USDC
                      </span>
                    </div>
                    {amountUsdc > usdcAvailable && (
                      <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        Saldo insuficiente para este pagamento
                      </p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Observações (opcional)</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Notas adicionais sobre o pagamento..."
                value={companyNotes}
                onChange={(e) => setCompanyNotes(e.target.value)}
                rows={3}
              />
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setCurrentStep("beneficiary")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit || loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Solicitar Pagamento
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
